/**
 * retryPolicy.ts
 *
 * Decides which request failures are worth retrying and how long to wait.
 *
 * This module is pure: no timers are started here, only durations computed,
 * so it can be unit-tested without fake timers.
 */
import { Code, ConnectError } from "@connectrpc/connect";
import { networkErrorPatterns } from "@/services/errorMessages";

/**
 * Reports whether a failed request should be retried.
 *
 * A failure is retryable when it looks transient: a bare `TypeError` (how
 * `fetch` reports a network failure), a transient Connect code, a message
 * matching a known network pattern, or a `TypeError` cause.
 *
 * @param error - The thrown value from a failed request.
 * @returns True when the request is worth retrying.
 */
export function isRetryableError(error: unknown): boolean {
  // Check if error itself is a TypeError (e.g., "TypeError: Failed to fetch")
  if (error instanceof TypeError) {
    return true;
  }

  if (!(error instanceof ConnectError)) {
    return false;
  }

  // Network errors typically have Code.Unknown or specific error messages
  const hasNetworkErrorCode =
    error.code === Code.Unknown ||
    error.code === Code.Unavailable ||
    error.code === Code.DeadlineExceeded;

  const hasNetworkErrorMessage = networkErrorPatterns.some((pattern) =>
    pattern.test(error.message)
  );

  const hasNetworkErrorCause = error.cause instanceof TypeError;

  return hasNetworkErrorCode || hasNetworkErrorMessage || hasNetworkErrorCause;
}

/**
 * Returns the exponential backoff delay before a given retry attempt.
 *
 * @param attempt - Zero-based attempt index.
 * @returns Delay in milliseconds: 1000, 2000, 4000, ...
 */
export function getRetryDelay(attempt: number): number {
  // Exponential backoff: 1s, 2s, 4s
  return 1000 * Math.pow(2, attempt);
}
